import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'redux/store';
import { closeEditTranslationComponent, translatedFileReplacement } from 'redux/actions/edit-translations';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { getCurrentUser } from 'redux/selectors/users';

import { getLectureComponent } from 'redux/selectors/lecture-components';
import t from 'react-translate';
import { css } from '@emotion/react';
import { AUTOMATED_TRANSLATION_LANGUAGES } from 'automated_translation/components/language-picker';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { FileReplacement } from './quiz-base/helper';
import { config } from '@config/config.json';

type EditableProps = {
  editInfo: TranslationInfo,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (disabled: boolean) => void,
  setIsLoading: (isLoading: boolean) => void,
};

const EditableFile = ({
  editInfo,
  onSaveRef,
  setSavingDisabled,
  setIsLoading,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.ATTACHMENT>;
  const currentAttachment = useSelector((state) => state.models.attachments[lectureComponent.attachment]);
  const catalogId = useSelector((state) => state.app.currentCatalogId);
  const fileType = currentAttachment.file.type;
  const [translatedFile, setTranslatedFile] = useState(null);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableFileStyle = css`
    width: 100%;
    justify-items: center;
    .description {
      padding: ${standardSpacing}px;
    }
  `;

  const save = useCallback(() => {
    const { novoedFile, ...translatedFilePayload } = translatedFile;
    const userLanguage = AUTOMATED_TRANSLATION_LANGUAGES.find((language) => language.isoCode === currentUser.translationPreferenceLanguage);
    const language = config.platformLanguages.find(lang => lang.toUpperCase() === userLanguage?.translationKey);
    const payload = {
      attachmentId: currentAttachment.id,
      language,
      attachment: translatedFilePayload,
      lectureComponentId: lectureComponent.id,
      catalogId,
    };
    setIsLoading(true);
    dispatch(translatedFileReplacement(payload)).then(() => {
      window.dispatchEvent(
        new CustomEvent('translated-files-updated', {
          detail: {
            attachments: [{
              translatedFrom: currentAttachment.id,
            }],
          },
        }),
      );
      dispatch(closeEditTranslationComponent());
    });
  }, [
    catalogId,
    currentAttachment,
    currentUser.translationPreferenceLanguage,
    dispatch,
    lectureComponent,
    setIsLoading,
    translatedFile,
  ]);

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (translatedFile !== null) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
      setIsLoading(false);
    });
  }, [translatedFile]);

  return (
    <div css={editableFileStyle}>
      <div className='description'>
        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.EDITABLE_FILE_DESCRIPTION() }
      </div>
      <FileReplacement setTranslatedFile={setTranslatedFile} fileType={fileType} />
    </div>
  );
};

export default EditableFile;
